import { useCallback, useEffect, useMemo, useState } from "react";
import { retrieveToken } from "../../services/tokenVault.js";
import DiffView from "./DiffView.jsx";

function humanizeOperation(id) {
  const s = String(id || "").replace(/_/g, " ").trim();
  if (!s) return "Operation";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function reversibilityBadgeClass(rev) {
  const r = String(rev || "").toLowerCase();
  if (r === "reversible") return "border-emerald-400/40 text-emerald-200/95 bg-emerald-500/10";
  if (r === "partially_reversible") return "border-amber-400/35 text-amber-100/95 bg-amber-500/10";
  if (r === "irreversible") return "border-rose-400/35 text-rose-100/95 bg-rose-500/10";
  return "border-white/15 text-slate-300 bg-white/[0.04]";
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   project: { projectId?: string, org?: { url?: string }, pendingApprovals?: unknown[] } | null,
 *   loadProject: () => Promise<unknown>,
 * }} props
 */
export default function ApprovalsDrawer({ open, onClose, project, loadProject, onSystemNotice }) {
  const [recentOpen, setRecentOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const projectId = project?.projectId;
  const orgUrl = project?.org?.url ? String(project.org.url).trim() : "";

  const refresh = useCallback(async () => {
    if (!projectId) return;
    await loadProject?.();
  }, [projectId, loadProject]);

  useEffect(() => {
    if (!open || !projectId) return;
    void refresh();
    const t = window.setInterval(() => {
      void refresh();
    }, 5000);
    return () => window.clearInterval(t);
  }, [open, projectId, refresh]);

  const lists = useMemo(() => {
    const raw = Array.isArray(project?.pendingApprovals) ? project.pendingApprovals : [];
    const pending = raw.filter((a) => a && (a.status === "pending" || a.status === "pending_restale"));
    const cutoff = Date.now() - 24 * 3600 * 1000;
    const recent = raw.filter((a) => {
      if (!a || a.status === "pending" || a.status === "pending_restale") return false;
      const ts = a.executedAt || a.decidedAt || a.failedAt || a.rolledBackAt || a.proposedAt;
      if (!ts) return false;
      return new Date(ts).getTime() >= cutoff;
    });
    return { pending, recent };
  }, [project?.pendingApprovals]);

  const apiToken = orgUrl ? retrieveToken(orgUrl) : null;

  const approve = async (approvalId) => {
    if (!projectId || !orgUrl || !apiToken) {
      window.alert("Reconnect Okta — SSWS token required to approve.");
      return;
    }
    setBusyId(approvalId);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/approvals/${encodeURIComponent(approvalId)}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ oktaUrl: orgUrl, apiToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        window.alert(data.error || data.errorSummary || "Approve failed");
        return;
      }
      if (data.systemMessage && typeof onSystemNotice === "function") {
        onSystemNotice(String(data.systemMessage));
      }
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (approvalId) => {
    if (!projectId) return;
    setBusyId(approvalId);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/approvals/${encodeURIComponent(approvalId)}/reject`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        window.alert(data.error || "Reject failed");
        return;
      }
      if (data.systemMessage && typeof onSystemNotice === "function") {
        onSystemNotice(String(data.systemMessage));
      }
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const rollback = async (approvalId) => {
    if (!projectId || !orgUrl || !apiToken) return;
    // eslint-disable-next-line no-alert -- minimal confirmation until modal component lands (Week 2 Day 3)
    if (!window.confirm("Undo this change using the stored snapshot?")) return;
    setBusyId(approvalId);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/approvals/${encodeURIComponent(approvalId)}/rollback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ oktaUrl: orgUrl, apiToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        window.alert(data.error || "Rollback failed");
        return;
      }
      if (data.systemMessage && typeof onSystemNotice === "function") {
        onSystemNotice(String(data.systemMessage));
      }
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close approvals"
        className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside
        className="fixed right-0 top-0 z-[130] flex h-full w-full max-w-md flex-col border-l border-white/[0.08] bg-[#050810] shadow-[-12px_0_48px_rgba(0,0,0,0.45)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="approvals-drawer-title"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-white/[0.06] px-4 py-3">
          <div>
            <h2 id="approvals-drawer-title" className="text-sm font-semibold text-slate-100">
              Approvals
            </h2>
            <p className="mt-0.5 text-[11px] text-slate-500">Review proposed Okta writes before they run.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-white/5 hover:text-slate-300"
            aria-label="Close drawer"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <section className="space-y-3">
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Pending</h3>
            {lists.pending.length === 0 ? (
              <p className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-6 text-center text-sm text-slate-500">
                No pending approvals.
              </p>
            ) : (
              lists.pending.map((a) => (
                <ApprovalCard
                  key={String(a.approvalId)}
                  entry={a}
                  busy={busyId === a.approvalId}
                  onApprove={() => approve(String(a.approvalId))}
                  onReject={() => reject(String(a.approvalId))}
                />
              ))
            )}
          </section>

          <section className="mt-8 border-t border-white/[0.06] pt-5">
            <button
              type="button"
              onClick={() => setRecentOpen((o) => !o)}
              className="flex w-full items-center justify-between text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-400"
            >
              <span>Recent — last 24h</span>
              <span className="text-slate-600">{recentOpen ? "▼" : "▶"}</span>
            </button>
            {recentOpen ? (
              <div className="mt-3 space-y-2">
                {lists.recent.length === 0 ? (
                  <p className="text-xs text-slate-600">Nothing in the last 24 hours.</p>
                ) : (
                  lists.recent.map((a) => (
                    <div
                      key={String(a.approvalId)}
                      className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-xs text-slate-400"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-slate-200">{a.summary || humanizeOperation(a.operationId)}</span>
                        <span className="shrink-0 rounded border border-white/10 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
                          {a.status}
                        </span>
                      </div>
                      {a.status === "executed" &&
                      a.rollbackSnapshot &&
                      (a.reversibility === "reversible" || a.reversibility === "partially_reversible") ? (
                        <button
                          type="button"
                          disabled={busyId === a.approvalId}
                          onClick={() => rollback(String(a.approvalId))}
                          className="mt-2 rounded border border-amber-400/35 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-100 hover:bg-amber-500/20 disabled:opacity-40"
                        >
                          Roll back
                        </button>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </section>

          <section className="mt-8 border-t border-white/[0.06] pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">History</p>
            <p className="mt-2 text-xs text-slate-600">Full audit export — deferred.</p>
          </section>
        </div>
      </aside>
    </>
  );
}

function ApprovalCard({ entry, busy, onApprove, onReject }) {
  const rev = entry.reversibility || "read";
  const needPartialConfirm = rev === "partially_reversible" || rev === "irreversible";
  const wrappedApprove = () => {
    if (needPartialConfirm && rev === "partially_reversible") {
      // eslint-disable-next-line no-alert -- tier-2 confirmation placeholder (Week 2 Day 3 modal)
      if (!window.confirm("This change is only partially reversible. Continue with approval?")) return;
    }
    if (needPartialConfirm && rev === "irreversible") {
      const typed = window.prompt('This operation is irreversible. Type APPROVE to continue. Type anything else to cancel.');
      if (typed !== "APPROVE") return;
    }
    onApprove();
  };

  return (
    <article className="rounded-xl border border-[rgb(var(--idaxis-accent-primary-rgb)/0.2)] bg-[rgb(var(--idaxis-accent-primary-rgb)/0.04)] p-3 shadow-[0_0_24px_rgb(var(--idaxis-accent-primary-rgb)/0.06)]">
      <header className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[13px] font-semibold text-slate-100">{humanizeOperation(entry.operationId)}</p>
          <p className="mt-0.5 text-xs text-slate-400">{entry.summary || ""}</p>
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${reversibilityBadgeClass(rev)}`}>
          {String(rev).replace(/_/g, " ")}
        </span>
      </header>
      {entry.status === "pending_restale" ? (
        <div className="mb-2 rounded-md border border-amber-400/25 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100/95">
          This resource changed since the proposal was created. Review the updated diff and approve again.
        </div>
      ) : entry.status === "superseded" ? (
        <div className="mb-2 rounded-md border border-amber-400/20 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100/90">
          Superseded by approval {entry.supersededBy || "—"}. Re-create if you still want to apply it.
        </div>
      ) : null}
      {entry.proposedAt ? (
        <p className="mb-2 text-[10px] text-slate-600">
          Proposed {formatRelative(entry.proposedAt)}
          {entry.rollbackHint ? (
            <>
              {" "}
              · <span className="text-slate-500">Rollback: {String(entry.rollbackHint)}</span>
            </>
          ) : null}
        </p>
      ) : null}
      <DiffView diff={entry.diff} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onReject}
          className="rounded-lg border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/10 disabled:opacity-40"
        >
          Reject
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={wrappedApprove}
          className="rounded-lg border border-[rgb(var(--idaxis-accent-primary-rgb)/0.45)] bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] px-3 py-1.5 text-xs font-bold text-[var(--idaxis-bg-primary)] shadow-[0_0_14px_rgb(var(--idaxis-accent-primary-rgb)/0.25)] hover:shadow-[0_0_20px_rgb(var(--idaxis-accent-primary-rgb)/0.35)] disabled:opacity-40"
        >
          {busy ? "Working…" : "Approve & execute"}
        </button>
      </div>
    </article>
  );
}

function formatRelative(iso) {
  try {
    const t = new Date(iso).getTime();
    const s = Math.round((Date.now() - t) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
  } catch {
    return "";
  }
}
